/**
 * oxman-hub 客户端 —— 取代原型自带的 Supabase。
 * 后端就是自家 hub:设备用 device bearer,人用 admin token,实时走 /ws。
 * 不引入任何云 SDK:每多一个云依赖,就多一个断链失能点。
 *
 * 这里的类型是 **hub 真正返回的形状**(server/internal/{devices,fleetnet,ledger} 的
 * json tag),不是控制台想要的形状。想要的形状在 hooks/useHubData.ts 里映射;
 * 映射时缺的字段一律给 null —— §9b:测不到不许写 0。
 */
const BASE = (import.meta.env.VITE_HUB_BASE ?? '').replace(/\/$/, '');
const TOKEN_KEY = 'oxman.hub.token';

export const auth = {
  get token() { return localStorage.getItem(TOKEN_KEY) ?? ''; },
  set token(v: string) { v ? localStorage.setItem(TOKEN_KEY, v) : localStorage.removeItem(TOKEN_KEY); },
  get signedIn() { return !!this.token; },
  signOut() { this.token = ''; },
};

/** 带状态码的错误:401 要跳登录,503 是"这个功能没配"(≠ 出错),展示层要能区分。 */
export class HubError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'HubError';
    this.status = status;
  }
}

export async function api<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const r = await fetch(`${BASE}/api/v1${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(auth.token ? { Authorization: `Bearer ${auth.token}` } : {}),
      ...(init.headers ?? {}),
    },
  });
  const text = await r.text();
  let body: unknown = null;
  try { body = text ? JSON.parse(text) : null; } catch { /* 非 JSON:原样带出,别当成数据 */ }
  if (!r.ok) {
    const msg = (body as { error?: string })?.error ?? `${r.status} ${r.statusText}`;
    throw new HubError(msg, r.status);
  }
  return body as T;
}

const post = <T>(path: string, body: unknown) =>
  api<T>(path, { method: 'POST', body: JSON.stringify(body) });

// ─── hub 返回的形状 ────────────────────────────────────────────────────────

/** devices.Device —— 注册表一行。**没有电量/温度/版本**:那些是遥测,hub v0 不存。 */
export interface HubDevice {
  id: string;
  name: string;
  kind: string;          // dog|glasses|phone|drone|other
  createdAt: number;     // unix 秒
  lastSeen?: number;     // unix 秒;缺席 = 从未用令牌调用过
  revokedAt?: number;    // 非 0 = 已吊销
}

/** fleetnet.Node —— headscale 视角的私有网节点。 */
export interface HubNode {
  id: string;
  name: string;
  ips: string[];
  online: boolean;
  lastSeen?: string;     // RFC3339
  deviceId?: string;     // 由 tag 反解, 与设备注册表对齐
}

/** hub.WGPeer —— 人的电脑走 wg 网关接入, headscale 不知道它。 */
export interface WGPeer {
  address: string;
  endpoint: string;
  lastSeen: number;
  online: boolean;
  rxBytes: number;
  txBytes: number;
}

/** fleetstate.Telemetry —— 指针字段在 JSON 里是"缺席",不是 0。 */
export interface HubTelemetry {
  deviceId: string;
  reportedAt: number;      // unix 秒, hub 收到的时刻(设备自报的时间不作数)
  battery?: number;
  temperature?: number;
  cpu?: number;
  gpu?: number;
  memory?: number;
  disk?: number;
  version?: string;
  capability?: string;     // full|desynced|offline_auto|minimal
  extra?: string;
  /** 位姿(map 帧, 米)。设备只在定位活着时才带 —— 缺席 = 此刻没有位姿。 */
  poseX?: number;
  poseY?: number;
  poseYaw?: number;
  mapId?: string;
  /** 收到【带位姿那一次】上报的时刻。与 reportedAt 分开, 见 fleetstate.PoseAt。 */
  poseAt?: number;
}

export interface HubAlert {
  id: number;
  level: 'critical' | 'warning' | 'info';
  kind: string;
  source: string;
  message: string;
  detail?: string;
  firstAt: number;
  lastAt: number;
  count: number;
  handledAt?: number;
  handledBy?: string;
  resolvedAt?: number;
}

export interface HubAuditEntry {
  id: number;
  at: number;
  actor: string;
  action: string;
  target: string;
  detail?: string;
}

/** /fleet/summary。比率都带分母来历:battery.reporting / battery.total。 */
export interface HubSummary {
  devices: {
    total: number; online: number; offline: number; revoked: number; neverSeen: number;
    byKind: Record<string, { total: number; online: number }>;
  };
  battery: { reporting: number; total: number; avg?: number; min?: number; max?: number };
  temperature: { reporting: number; total: number; max?: number };
  tasks?: { open: number; done: number; failed: number; unclaimedUnits: number; runningUnits: number };
  alerts?: Record<string, number>;
  alertsUnavailable?: boolean;
  network: { configured: boolean; ok?: boolean; detail?: string; nodes?: number };
  generatedAt: number;
}

export interface HubTask {
  id: number;
  createdAt: number;
  updatedAt: number;
  goal: string;
  kind: string;
  status: string;        // open|done|failed|cancelled
  mapId: string;
  createdBy: string;
}

export interface HubUnit {
  id: number;
  taskId: number;
  parentId?: number;
  seq: number;
  kind: string;
  label: string;
  geometry?: string;
  mapId?: string;
  state: string;         // unclaimed|claimed|running|done|failed
  claimedBy?: string;
  claimSeq: number;
  heartbeatAt?: number;
  leaseMs: number;
  splitLimit: number;
  cost?: number;
  evidence?: string;
  result?: string;
  failReason?: string;
  failCount?: number;
}

export interface UnitSpec {
  kind: string;
  label: string;
  geometry?: string;
  seq: number;
  map_id?: string;
  lease_ms?: number;
  split_limit?: number;
}

// ─── knowledge(设备知识汇聚,PIPL 过滤后的地点/物体/事实)────────────────
// 人物/对话/媒体永远不上云 —— 狗侧 ksync 过滤,hub 白名单兜底。
// 见 server/internal/hub/knowledge_routes.go 与 design-knowledge-fleet-sync。

export interface HubKnowledgePlace {
  deviceId: string;
  mapId: string;
  name: string;
  x: number;
  y: number;
  yaw: number;
  descr?: string;
  confidence?: number;
}

export interface HubKnowledgeObject {
  deviceId: string;
  instId: number;
  name: string;
  mapId: string;
  x: number;
  y: number;
  nObs: number;
  firstSeen: number;   // 狗自报,可能 1970(无 RTC)—— 展示层判"未知"
  lastSeen: number;
  nearestPlace?: string;
}

export interface HubKnowledgeFact {
  deviceId: string;
  subject: string;
  predicate: string;
  object: string;
  text: string;
  confidence: number;
}

export interface HubKnowledgeDeviceMeta {
  deviceId: string;
  name?: string;
  syncedAt: number;    // hub 打点,可信
  nPlaces: number;
  nObjects: number;
  nFacts: number;
  dropped?: { places: number; attrs: number; facts: number };
}

export interface HubKnowledgeSummary {
  devices: HubKnowledgeDeviceMeta[];
  neverPushed: string[];          // 注册了但还没推过知识的设备(真话,不是 0)
  totals: { places: number; objects: number; facts: number };
  reportedDevices: number;        // totals 的分母来历
  duplicateCandidates: { kind: 'place' | 'object'; name: string; nDevices: number; devices: string[] }[];
}

export interface HubKnowledgeGraph {
  devices: { id: string; name?: string; syncedAt: number; nPlaces: number; nObjects: number; nFacts: number }[];
  places: HubKnowledgePlace[];
  objects: HubKnowledgeObject[];
  facts: HubKnowledgeFact[];
}

// ─── 现场报表(设备推的 mission.Report)──────────────────────────────────────
// ★ 这些数【全部由设备侧算】, hub 与前端都不重算 —— 成功率的分母、按层归因、
//   MTBI 的时间窗都有讲究(设备那边专门处理过无 RTC 导致 1970 时间戳把窗口拉到
//   495701 小时、MTBI 假成 449h)。前端只负责显示, 顺带把分母一起显示出来。

export interface FieldReport {
  deployments: number;
  cleanShutdowns: number;
  sites: string[] | null;
  windowStart: number;
  windowEnd: number;
  spanHours: number;
  clockSkewedDeployments?: number;
  missions: number;
  /** 真正到达机器人的那些(有执行器跑过)。闲聊不算 —— 论文那条 ≥50 条指令的线指的是它。 */
  actionable: number;
  success: number;
  failed: number;
  cancelled: number;
  notfound: number;
  aborted: number;
  ignored: number;
  open: number;
  successRate: number;
  distanceKm: number;
  failuresByLayer: Record<string, number> | null;
  interventions: number;
  interventionsBySeverity: Record<string, number> | null;
  mtbiHours: number;
  memWrites: number;
  memWritesByGate: Record<string, number> | null;
  memWritesStale: number;
  healthSnapshots: number;
  /** 设备自己列出的"离一份能交的报告还差什么"。空 = 齐了。 */
  gaps: string[] | null;
}

export interface HubDeviceReport {
  deviceId: string;
  updatedAt: number;
  payload: { schema: number; all: FieldReport; d30: FieldReport };
}

// ─── 地图栅格(设备推的占据栅格 PNG)────────────────────────────────────────

/** 实时位姿(hub 内存里的最新一条)。at 是**毫秒**, 且是 hub 收到的时刻。 */
export interface HubLivePose {
  deviceId: string;
  x: number;
  y: number;
  yaw: number;
  mapId: string;
  at: number;
}

export interface HubMapGrid {
  deviceId: string;
  mapId: string;
  updatedAt: number;   // hub 收到的时刻(秒)。值和值的时间一起活, 同位姿。
  width: number;
  height: number;
  resolution: number;  // 米/像素(已按下采样倍数放大过)
  originX: number;     // map 帧里, 像素 (0,0) 对应的米
  originY: number;
  known: number;       // 已观测格占比 —— 任何"覆盖率"说法的分母来历
}

/**
 * 栅格 PNG 的地址。★ 不能直接给 <img src>: hub 是 fail-closed 的, 图片请求
 * 不会带 Authorization 头, 必然 401。所以取图要用 fetch + Bearer, 再转成
 * blob URL。这个函数只拼地址, 取图在 useMapGrid 里。
 */
export function mapGridURL(deviceId: string, mapId: string): string {
  return `${BASE}/api/v1/maps/grid.png?device=${encodeURIComponent(deviceId)}&map=${encodeURIComponent(mapId)}`;
}

// ─── 视频(中继上有哪几路可看)──────────────────────────────────────────────
// 名单的单一事实源是【中继配置】, 前端不抄。三个字段的含义严格区分:
//   在清单里 ≠ 有画面;pulling=0 是懒加载的正常态(没人看就不连狗);
//   consumers = 此刻几个人在看。"有没有信号"只有连上去才知道 —— 见 hub/video.go。

export interface HubVideoStream {
  stream: string;        // 设备侧的流名, 如 go2_front
  relayName: string;     // 中继侧的流名 = {设备id}_{流名}
  /** 中继为这一路【配了】几个上游(通常 1)。★ 不等于"在拉" —— 见下一行。 */
  sources: number;
  /** 此刻真正连着的上游数。0 是懒加载的正常态(没人看就不连设备)。
   *  ★ 这两个数曾经是一个数, 结果在一条连接都没有时报"上游 1"(0808 实测,
   *  ss 证明到狗 8554 是 0)—— 见 server/internal/hub/video.go 里那段。 */
  pulling: number;
  consumers: number;     // 此刻几个观看者
  bytesRecv?: number;    // 缺席 = 此刻没在拉
}

export interface HubVideoDevice {
  deviceId: string;
  streams: HubVideoStream[];
}

// ─── 具体接口 ─────────────────────────────────────────────────────────────

export const hub = {
  whoami: () => api<{ kind: string; deviceId: string; terminal: string }>('/whoami'),

  // ─── passkey 登录 ────────────────────────────────────────────────────────
  // 这四条**不带凭证也能调** —— 走到它们的人此刻手上一把都没有, 那正是他来的原因。
  // 服务端对应的豁免名单在 hub/auth_routes.go 的 isAuthPublicPath()。
  auth: {
    registerBegin: (code: string, deviceName: string) =>
      post<{ flowId: string; publicKey: Record<string, unknown> }>(
        '/auth/register/begin', { code, deviceName }),
    registerFinish: (flowId: string, credential: unknown) =>
      post<{ token: string; name: string }>('/auth/register/finish', { flowId, credential }),
    loginBegin: () =>
      post<{ flowId: string; publicKey: Record<string, unknown> }>('/auth/login/begin', {}),
    loginFinish: (flowId: string, credential: unknown) =>
      post<{ token: string; name: string }>('/auth/login/finish', { flowId, credential }),
    logout: () => post<{ ok: boolean }>('/auth/logout', {}),
    // 管理: 看谁有哪些 passkey, 以及吊销。要已登录身份。
    people: () => api<{
      people: { id: string; name: string; createdAt: number; disabledAt?: number }[] | null;
      credentials: {
        id: string; personId: string; name: string;
        createdAt: number; lastUsed?: number; revokedAt?: number;
      }[] | null;
    }>('/auth/people'),
    revokeCredential: (id: string) => post<{ ok: boolean }>('/auth/credentials/revoke', { id }),
    disablePerson: (id: string) => post<{ ok: boolean }>('/auth/people/disable', { id }),
  },

  devices: () => api<{
    devices: HubDevice[] | null;
    nodes: HubNode[] | null;
    /** 键缺席 = 那台设备从未上报过遥测 */
    telemetry?: Record<string, HubTelemetry>;
    telemetryError?: string;
    /** 键缺席 = 这台设备此刻没有实时位姿(不是"在原点")。hub 只给还新鲜的那些。 */
    livePoses?: Record<string, HubLivePose>;
  }>('/devices'),
  device: (id: string) => api<{
    device: HubDevice; node?: HubNode; telemetry?: HubTelemetry; alerts?: HubAlert[];
  }>(`/devices/${encodeURIComponent(id)}`),
  /** 设备用自己的令牌上报;人的 admin 令牌调这个会被 403(身份从认证推导)。 */
  reportTelemetry: (t: Partial<HubTelemetry>) => post<{ ok: boolean }>('/devices/telemetry', t),
  registerDevice: (d: { id: string; name: string; kind: string }) =>
    post<{ device: HubDevice; token: string; note: string }>('/devices', d),
  rotateDevice: (id: string) => post<{ token: string; note: string }>('/devices/rotate', { id }),
  revokeDevice: (id: string) => post<{ ok: boolean }>('/devices/revoke', { id }),

  /** 三态: wgPeers 缺席 = 没配 wg 网关;[] = 配了此刻没人连;wgError = 读失败(**不是**没人连)。 */
  netNodes: () => api<{ nodes: HubNode[] | null; wgPeers?: WGPeer[]; wgError?: string }>('/net/nodes'),
  netHealth: () => api<{ ok: boolean; configured: boolean; detail?: string; note?: string }>('/net/health'),

  alerts: (opts: { level?: string; source?: string; all?: boolean; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (opts.level) q.set('level', opts.level);
    if (opts.source) q.set('source', opts.source);
    if (opts.all) q.set('all', '1');
    if (opts.limit) q.set('limit', String(opts.limit));
    const qs = q.toString();
    return api<{ alerts: HubAlert[]; counts: Record<string, number> }>(`/alerts${qs ? `?${qs}` : ''}`);
  },
  ackAlert: (id: number) => post<{ ok: boolean }>('/alerts/ack', { id }),
  summary: () => api<HubSummary>('/fleet/summary'),
  audit: (limit = 100) => api<{ entries: HubAuditEntry[] }>(`/audit?limit=${limit}`),

  tasks: (status?: string) =>
    api<{ tasks: HubTask[] }>(`/ledger/tasks${status ? `?status=${encodeURIComponent(status)}` : ''}`),
  task: (id: number) => api<{ task: HubTask; units: HubUnit[] }>(`/ledger/task?id=${id}`),
  createTask: (t: { goal: string; kind?: string; mapId?: string; units: UnitSpec[] }) =>
    post<{ taskId: number }>('/ledger/task', t),

  /** 各设备最新的现场报表(原样透出设备算好的数)。 */
  reports: () => api<{ reports: HubDeviceReport[] }>('/reports'),

  /** 地图栅格的元信息(不含像素)。像素走 mapGridURL() 的 <img>。 */
  mapGrids: () => api<{ grids: HubMapGrid[] }>('/maps/grids'),

  /** 视频墙的名单。503 = 没部署中继;502 = 中继不可达(≠"没有流")。 */
  videoStreams: () => api<{ devices: HubVideoDevice[]; unassigned: string[] }>('/video/streams'),

  /** 知识图谱:不带 device = 全设备并集(群体);带 device = 单体。admin only。 */
  knowledgeGraph: (device?: string) =>
    api<HubKnowledgeGraph>(`/knowledge/graph${device ? `?device=${encodeURIComponent(device)}` : ''}`),
  knowledgeSummary: () => api<HubKnowledgeSummary>('/knowledge/summary'),
};

/**
 * 实时:hub 的 /ws 跨设备事件广播。
 *
 * 令牌走**子协议**而不是请求头:浏览器的 WebSocket API 根本不让设 header,
 * 而 hub 是 fail-closed 的(除 /health 外全要鉴权)—— 不带令牌的握手会 401,
 * 于是"实时更新"永远静默地不工作。也不用 `?token=`:查询串会落进 Caddy 访问日志。
 * hub 侧对应的是 hub.bearer() + upgrader.Subprotocols(见 internal/hub)。
 */
export function connectEvents(onEvent: (e: unknown) => void): WebSocket {
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  const host = BASE || `${proto}://${location.host}`;
  const url = `${host.replace(/^http/, 'ws')}/ws`;
  const ws = auth.token
    ? new WebSocket(url, ['bearer', auth.token])
    : new WebSocket(url);
  ws.onmessage = (m) => { try { onEvent(JSON.parse(m.data)); } catch { /* 忽略非 JSON 帧 */ } };
  return ws;
}
